import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fmtDate, fmtDateTime, fmtDateShort } from "@/lib/time";
import {
  ShieldCheck, ShieldAlert, ShieldX, Users, CheckCircle2, Clock,
  X, CreditCard, Fingerprint, Building2, ScrollText, ChevronDown,
  ChevronRight, Download, RefreshCw, Search, Filter, AlertTriangle,
  ThumbsUp, ThumbsDown, MessageSquare,
} from "lucide-react";
const API = process.env.REACT_APP_BACKEND_URL;

const PARTNER_TYPE_LABELS = {
  sales_partner: "Sales Partner",
  sales_manager: "Sales Manager",
  zonal_partner: "Zonal Partner",
  regional_partner: "Regional Partner",
  national_partner: "National Partner",
};

function StatusBadge({ verified, label }) {
  return verified ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
      <CheckCircle2 className="w-2.5 h-2.5" />{label}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20">
      <Clock className="w-2.5 h-2.5" />Pending
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-2xl font-bold text-foreground">{value ?? "—"}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

const TYPE_META = {
  pan:     { label: "PAN",     icon: CreditCard,   color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  bank:    { label: "Bank",    icon: Building2,     color: "bg-violet-500/10 text-violet-600 border-violet-500/20" },
  aadhaar: { label: "Aadhaar", icon: Fingerprint,   color: "bg-orange-500/10 text-orange-600 border-orange-500/20" },
};

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || { label: type, icon: ShieldAlert, color: "bg-muted text-muted-foreground border-border" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${meta.color}`}>
      <Icon className="w-2.5 h-2.5" />{meta.label}
    </span>
  );
}

function ProviderBadge({ provider }) {
  return provider === "perfios" ? (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 font-medium">
      <ShieldCheck className="w-2.5 h-2.5" />Perfios
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium">
      Mock
    </span>
  );
}

function ResultBadge({ result }) {
  return result === "pass" ? (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/25">
      <CheckCircle2 className="w-2.5 h-2.5" />Pass
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-red-500/10 text-red-700 border border-red-500/25">
      <X className="w-2.5 h-2.5" />Fail
    </span>
  );
}

function MatchScoreBadge({ score, method }) {
  if (score === null || score === undefined) return <span className="text-[10px] text-muted-foreground">—</span>;
  const pct = Math.round(score * 100);
  const label = method === "exact" ? "100% Exact"
    : method === "fuzzy"  ? `${pct}% Fuzzy`
    : method === "subset" ? `${pct}% Subset`
    : `${pct}%`;
  const color = pct >= 90
    ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/20"
    : pct >= 75
    ? "bg-amber-500/10 text-amber-700 border-amber-500/20"
    : "bg-orange-500/10 text-orange-700 border-orange-500/20";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${color}`}
      title={`Match method: ${method || "—"}`}>
      {label}
    </span>
  );
}

function LogRow({ log }) {
  const [expanded, setExpanded] = useState(false);

  const details = [];
  if (log.verification_type === "pan") {
    if (log.input?.pan) details.push({ label: "PAN", value: log.input.pan });
    if (log.input?.name) details.push({ label: "Name Submitted", value: log.input.name });
    if (log.name_on_pan) details.push({ label: "Name on PAN", value: log.name_on_pan });
    if (log.pan_type) details.push({ label: "PAN Type", value: log.pan_type });
    if (log.pan_status) details.push({ label: "PAN Status", value: log.pan_status });
    if (log.name_match !== null && log.name_match !== undefined)
      details.push({ label: "Name Match", value: log.name_match ? "Yes" : "No" });
    if (log.name_match_score !== null && log.name_match_score !== undefined)
      details.push({ label: "Match Score", value: `${Math.round(log.name_match_score * 100)}% (${log.name_match_method || "—"})` });
  }
  if (log.verification_type === "bank") {
    if (log.input?.account_masked) details.push({ label: "Account (masked)", value: log.input.account_masked });
    if (log.input?.ifsc) details.push({ label: "IFSC", value: log.input.ifsc });
    if (log.input?.account_name) details.push({ label: "Name Submitted", value: log.input.account_name });
    if (log.account_holder_verified) details.push({ label: "Name at Bank", value: log.account_holder_verified });
    if (log.bank_status) details.push({ label: "Bank Status", value: log.bank_status });
    if (log.name_match !== null && log.name_match !== undefined)
      details.push({ label: "Name Match", value: log.name_match ? "Yes" : "No" });
    if (log.name_match_score !== null && log.name_match_score !== undefined)
      details.push({ label: "Match Score", value: `${Math.round(log.name_match_score * 100)}% (${log.name_match_method || "—"})` });
  }
  if (log.verification_type === "auto_approval") {
    if (log.input?.pan_name) details.push({ label: "PAN Name", value: log.input.pan_name });
    if (log.input?.bank_name) details.push({ label: "Bank Name", value: log.input.bank_name });
    if (log.name_match_score !== null && log.name_match_score !== undefined)
      details.push({ label: "Cross-Match Score", value: `${Math.round(log.name_match_score * 100)}% (${log.name_match_method || "—"})` });
  }
  if (log.verification_type === "aadhaar") {
    if (log.input?.aadhaar_masked) details.push({ label: "Aadhaar (masked)", value: log.input.aadhaar_masked });
  }
  if (log.error) details.push({ label: "Error", value: log.error, error: true });
  details.push({ label: "Verified by", value: log.verified_by || "—" });

  return (
    <>
      <tr
        className="border-b border-border/50 hover:bg-muted/20 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
        data-testid={`log-row-${log.id}`}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            {expanded ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
            <span className="text-[11px] text-foreground font-medium">{log.partner_name || "—"}</span>
          </div>
          <div className="text-[10px] text-muted-foreground pl-4">{log.partner_email}</div>
        </td>
        <td className="px-4 py-3"><TypeBadge type={log.verification_type} /></td>
        <td className="px-4 py-3"><ProviderBadge provider={log.provider} /></td>
        <td className="px-4 py-3"><ResultBadge result={log.result} /></td>
        <td className="px-4 py-3">
          <MatchScoreBadge score={log.name_match_score} method={log.name_match_method} />
        </td>
        <td className="px-4 py-3 text-[11px] text-muted-foreground whitespace-nowrap">
          {log.timestamp ? fmtDateTime(log.timestamp) : "—"}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/10 border-b border-border/50">
          <td colSpan={6} className="px-8 py-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-2.5">
              {details.map((d, i) => (
                <div key={i}>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">{d.label}</div>
                  <div className={`text-[12px] font-medium font-mono ${d.error ? "text-red-600" : "text-foreground"}`}>{d.value}</div>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── KYC Comparison Panels ──────────────────────────────────────────────────────

/** Single detail chip */
function DetailChip({ label, value, ok }) {
  if (!value || value === "—") return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg border border-border/60">
      <div>
        <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
        <div className="text-[12px] font-mono text-foreground">{value}</div>
      </div>
      {ok === true  && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto shrink-0" />}
      {ok === false && <AlertTriangle className="w-3.5 h-3.5 text-red-500 ml-auto shrink-0" />}
    </div>
  );
}

/** Three-way name comparison — partner entered | PAN card | Bank records */
function KycNameComparison({ partnerName, panName, bankName, panMatch, bankMatch, crossScore, kycStatus }) {
  const pct = crossScore != null ? Math.round(crossScore * 100) : null;
  const isApproved = kycStatus === "verified";
  const isRejected = kycStatus === "rejected";

  // Risk level based on cross-match score
  const riskLevel = pct != null
    ? pct < 50 ? "high" : pct < 80 ? "medium" : "low"
    : (isApproved ? "low" : null);

  const riskConfig = {
    high:   { label: "HIGH RISK",   bg: "bg-red-500/8",    border: "border-red-400",   text: "text-red-700",    badge: "bg-red-500 text-white" },
    medium: { label: "MEDIUM RISK", bg: "bg-amber-500/8",  border: "border-amber-400", text: "text-amber-700",  badge: "bg-amber-500 text-white" },
    low:    { label: "MATCH VERIFIED", bg: "bg-emerald-500/8", border: "border-emerald-400", text: "text-emerald-700", badge: "bg-emerald-500 text-white" },
  };
  const risk = riskConfig[riskLevel] || null;

  const matchBadge = (matched) => {
    if (matched === null || matched === undefined) return null;
    if (matched)
      return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 font-medium">match</span>;
    return <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-700 font-medium">mismatch</span>;
  };

  return (
    <div className={`rounded-xl border-2 overflow-hidden ${risk ? risk.border : "border-border"}`} data-testid="kyc-name-comparison">
      {/* Header with risk badge */}
      <div className={`px-4 py-2.5 border-b border-border/60 flex items-center justify-between ${risk ? risk.bg : "bg-muted/30"}`}>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">Name Comparison</span>
        {risk && (
          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider ${risk.badge}`}>
            {risk.label}
            {pct != null && ` · ${pct}%`}
          </span>
        )}
      </div>

      {/* Three columns */}
      <div className="grid grid-cols-3 divide-x divide-border/60">
        {/* Col 1 — Partner entered */}
        <div className="px-3 py-3 flex flex-col gap-1">
          <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Partner Entered</div>
          <div className="font-mono text-[12px] font-semibold text-foreground break-words">{partnerName || "—"}</div>
          <div className="text-[9px] text-muted-foreground/60 mt-auto">as submitted</div>
        </div>

        {/* Col 2 — PAN card */}
        <div className="px-3 py-3 flex flex-col gap-1">
          <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Name on PAN</div>
          <div className={`font-mono text-[12px] font-semibold break-words ${panName ? "text-foreground" : "text-muted-foreground/50 italic"}`}>
            {panName || "—"}
          </div>
          {panName && matchBadge(panMatch)}
        </div>

        {/* Col 3 — Bank records */}
        <div className="px-3 py-3 flex flex-col gap-1">
          <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Name at Bank</div>
          <div className={`font-mono text-[12px] font-semibold break-words ${bankName ? "text-foreground" : "text-muted-foreground/50 italic"}`}>
            {bankName || "—"}
          </div>
          {bankName && matchBadge(bankMatch)}
        </div>
      </div>

      {/* Cross-match result bar */}
      {(panName && bankName) && (
        <div className={`px-4 py-2.5 border-t border-border/60 flex items-center gap-2 text-[11px] font-medium ${
          riskLevel === "high"   ? "bg-red-500/8 text-red-700"
          : riskLevel === "medium" ? "bg-amber-500/8 text-amber-700"
          : riskLevel === "low"    ? "bg-emerald-500/8 text-emerald-700"
          : "bg-muted/20 text-muted-foreground"
        }`}>
          {riskLevel === "high"   ? <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
           : riskLevel === "low"  ? <CheckCircle2  className="w-3.5 h-3.5 shrink-0" />
           : <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
          <span>PAN ↔ Bank cross-match:</span>
          <span className="font-bold">
            {pct != null ? `${pct}% — ` : ""}
            {riskLevel === "low"    ? "Auto-Approved"
             : riskLevel === "high" ? "High Risk — Rejected"
             : isRejected           ? "Rejected"
             : "Pending Admin Review"}
          </span>
        </div>
      )}
      {/* High risk warning strip */}
      {riskLevel === "high" && panName && bankName && (
        <div className="px-4 py-2.5 bg-red-500/5 border-t border-red-400/30 flex items-start gap-2 text-[11px] text-red-700">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>Names on PAN and Bank records belong to <strong>different individuals</strong>. Partner may have submitted someone else's documents.</span>
        </div>
      )}
    </div>
  );
}

// ── KycComparePanel: Read-only 2-column verification result display ──────────
function KycComparePanel({ title, rows, status, provider }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="px-4 py-2.5 bg-muted/30 border-b border-border flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">{title}</span>
        <div className="flex items-center gap-1.5">
          {status === "verified" && (
            <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
              <CheckCircle2 className="w-3 h-3" />Verified
            </span>
          )}
          {provider && <ProviderBadge provider={provider} />}
        </div>
      </div>
      <div className="divide-y divide-border/50">
        {rows.map((row, i) => (
          <div key={i} className="px-4 py-2.5">
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">{row.label}</div>
            <div className={`grid gap-4 items-start ${row.govtOnly ? "grid-cols-1" : "grid-cols-2"}`}>
              {!row.govtOnly && (
                <div>
                  <div className="text-[9px] text-muted-foreground/70 mb-0.5">Partner Submitted</div>
                  <div className="text-[12px] font-mono text-foreground break-all">{row.submitted || "—"}</div>
                </div>
              )}
              <div>
                <div className="text-[9px] text-muted-foreground/70 mb-0.5">
                  {row.govtOnly ? "Govt / Bank Record" : "As per Govt / Bank"}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="text-[12px] font-mono text-foreground break-all">{row.govt || "—"}</div>
                  {row.match === true  && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                  {row.match === false && <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** CrossMatchPanel — 3-way name comparison with status strip */
function CrossMatchPanel({ partnerName, panName, bankName, kycStatus, score, reason }) {
  return (
    <div className="space-y-2">
      <KycNameComparison
        partnerName={partnerName}
        panName={panName}
        bankName={bankName}
        crossScore={score}
        kycStatus={kycStatus}
      />
      {reason && (
        <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-[11px] border ${
          kycStatus === "admin_review"    ? "bg-amber-500/8 border-amber-500/20 text-amber-700"
          : kycStatus === "rejected"      ? "bg-red-500/8 border-red-500/20 text-red-700"
          : kycStatus === "query_raised"  ? "bg-blue-500/8 border-blue-500/20 text-blue-700"
          : "bg-muted border-border text-muted-foreground"
        }`}>
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{reason}</span>
        </div>
      )}
    </div>
  );
}

export default function SMKYC() {
  const [mainTab, setMainTab] = useState("partners");  // "partners" | "log"

  // Partners view state
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reviewDeciding, setReviewDeciding] = useState(null);
  const [kycConfig, setKycConfig] = useState({ live_mode: false, provider: "mock" });
  // Modal action states
  const [showQueryInput, setShowQueryInput] = useState(false);
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [queryNote, setQueryNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");

  // Audit log state
  const [logs, setLogs] = useState([]);
  const [logSummary, setLogSummary] = useState({ total: 0, pass_count: 0, fail_count: 0 });
  const [logLoading, setLogLoading] = useState(false);
  const [logTypeFilter, setLogTypeFilter] = useState("all");
  const [logResultFilter, setLogResultFilter] = useState("all");
  const [logSearch, setLogSearch] = useState("");

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { kyc_status: filter } : {};
      const [r, cfgR] = await Promise.all([
        axios.get(`${API}/api/sm/kyc/partners`, { params, withCredentials: true }),
        axios.get(`${API}/api/sm/kyc/config`, { withCredentials: true })
          .catch(() => ({ data: { live_mode: false, provider: "mock" } })),
      ]);
      setPartners(r.data.partners || []);
      setSummary(r.data.summary);
      setKycConfig(cfgR.data);
    } catch { toast.error("Failed to load KYC data"); }
    finally { setLoading(false); }
  }, [filter]);

  const loadLogs = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = {};
      if (logTypeFilter !== "all") params.verification_type = logTypeFilter;
      if (logResultFilter !== "all") params.result = logResultFilter;
      params.limit = 200;
      const r = await axios.get(`${API}/api/sm/kyc/logs`, { params, withCredentials: true });
      setLogs(r.data.logs || []);
      setLogSummary({ total: r.data.total, pass_count: r.data.pass_count, fail_count: r.data.fail_count });
    } catch { toast.error("Failed to load verification logs"); }
    finally { setLogLoading(false); }
  }, [logTypeFilter, logResultFilter]);

  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { if (mainTab === "log") loadLogs(); }, [mainTab, loadLogs]);

  const openModal = (partner) => {
    setSelected(partner);
    setShowQueryInput(false);
    setShowRejectInput(false);
    setQueryNote("");
    setRejectNote("");
  };

  const handleAdminDecide = async (partnerId, decision, note = "") => {
    setReviewDeciding(partnerId);
    try {
      await axios.post(`${API}/api/sm/kyc/admin-review/${partnerId}/decide`,
        { decision, note }, { withCredentials: true });
      const msg = decision === "approve" ? "Partner approved and activated"
        : decision === "query"  ? "Query sent to partner"
        : "Partner KYC rejected";
      toast.success(msg);
      if (selected?.id === partnerId) setSelected(null);
      loadPartners();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Decision failed");
    } finally { setReviewDeciding(null); }
  };

  const [reVerifying, setReVerifying] = useState(false);

  const handleReVerify = async () => {
    if (!selected) return;
    setReVerifying(true);
    try {
      const r = await axios.post(`${API}/api/sm/partners/${selected.id}/re-verify-kyc`,
        {}, { withCredentials: true });
      toast.success(`Re-verification complete — KYC: ${r.data.results?.kyc_decision || "pending"}`);
      loadPartners();
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-verification failed");
    } finally { setReVerifying(false); }
  };

  const kycIcon = (p) => {
    if (p.kyc_status === "verified")      return <ShieldCheck  className="w-4 h-4 text-emerald-600" />;
    if (p.kyc_status === "admin_review")  return <Clock        className="w-4 h-4 text-amber-500" />;
    if (p.kyc_status === "rejected")      return <ShieldX      className="w-4 h-4 text-red-500" />;
    if (p.kyc_status === "query_raised")  return <MessageSquare className="w-4 h-4 text-blue-500" />;
    if (p.pan_verified || p.aadhaar_verified || p.bank_verified) return <ShieldAlert className="w-4 h-4 text-amber-500" />;
    return <ShieldX className="w-4 h-4 text-muted-foreground" />;
  };

  // CSV export for logs
  const exportCSV = () => {
    const filtered = filteredLogs;
    if (!filtered.length) { toast.error("No logs to export"); return; }
    const headers = ["Timestamp", "Partner", "Email", "Type", "Provider", "Result", "Match Score", "Match Method", "Error", "Verified By", "PAN", "Account Masked", "IFSC", "Name on PAN", "PAN Type", "Bank Status", "Name Match"];
    const rows = filtered.map(l => [
      l.timestamp ? fmtDateTime(l.timestamp) : "",
      l.partner_name || "", l.partner_email || "",
      l.verification_type || "", l.provider || "", l.result || "",
      l.name_match_score !== null && l.name_match_score !== undefined ? `${Math.round(l.name_match_score * 100)}%` : "",
      l.name_match_method || "",
      l.error || "", l.verified_by || "",
      l.input?.pan || "", l.input?.account_masked || "", l.input?.ifsc || "",
      l.name_on_pan || "", l.pan_type || "", l.bank_status || "",
      l.name_match !== null && l.name_match !== undefined ? (l.name_match ? "Yes" : "No") : "",
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kyc_verification_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("CSV exported");
  };

  const filteredLogs = logs.filter(l =>
    !logSearch || l.partner_name?.toLowerCase().includes(logSearch.toLowerCase()) ||
    l.partner_email?.toLowerCase().includes(logSearch.toLowerCase())
  );

  const PARTNER_FILTERS = [
    { key: "all",          label: "All" },
    { key: "admin_review", label: "Pending Review" },
    { key: "query_raised", label: "Query Raised" },
    { key: "verified",     label: "Verified" },
    { key: "pending",      label: "Pending" },
    { key: "rejected",     label: "Rejected" },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sm-kyc">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">Partner KYC</h1>
          <p className="text-sm text-muted-foreground mt-0.5">PAN · Aadhaar · Bank verification</p>
        </div>
        {kycConfig.live_mode ? (
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1 shrink-0">
            <ShieldCheck className="w-3 h-3" />Live Mode — Perfios
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-full px-2.5 py-1 shrink-0">
            <ShieldAlert className="w-3 h-3" />Mock Mode
          </span>
        )}
      </div>

      {/* Main tabs: Partners | Audit Log */}
      <div className="flex border-b border-border gap-0" data-testid="kyc-main-tabs">
        {[
          { key: "partners", label: "Partners", icon: Users },
          { key: "log", label: "Verification Log", icon: ScrollText },
        ].map(t => (
          <button key={t.key} onClick={() => setMainTab(t.key)}
            data-testid={`kyc-main-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-5 py-2.5 text-[12px] font-medium border-b-2 transition-all -mb-px ${
              mainTab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* ── Partners view ── */}
      {mainTab === "partners" && (
        <>
          {/* Summary KPIs */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard label="Total Partners"  value={summary.total}        icon={Users}         color="bg-primary/10 text-primary" />
              <KpiCard label="KYC Verified"    value={summary.verified}     icon={ShieldCheck}   color="bg-emerald-500/10 text-emerald-600" />
              <KpiCard label="Pending Review"  value={summary.admin_review} icon={Clock}         color="bg-amber-500/10 text-amber-600" />
              <KpiCard label="Query Raised"    value={summary.query_raised ?? 0} icon={MessageSquare} color="bg-blue-500/10 text-blue-600" />
              <KpiCard label="Partial KYC"     value={summary.partial}      icon={ShieldAlert}   color="bg-violet-500/10 text-violet-600" />
              <KpiCard label="Rejected"        value={summary.rejected}     icon={ShieldX}       color="bg-red-500/10 text-red-600" />
            </div>
          )}

          <div className="flex bg-muted rounded-xl p-1 gap-1 w-fit">
            {PARTNER_FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`kyc-filter-${f.key}`}
                className={`px-4 py-2 rounded-lg text-[12px] font-medium transition-all ${filter === f.key ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Mode notice */}
          {kycConfig.live_mode ? (
            <div className="flex items-start gap-2 px-4 py-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl text-[12px] text-emerald-700">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>Live Mode Active</strong> — PAN and Bank verifications are calling Perfios Hub APIs.</span>
            </div>
          ) : (
            <div className="flex items-start gap-2 px-4 py-3 bg-amber-500/5 border border-amber-500/20 rounded-xl text-[12px] text-amber-700">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>Mock Mode Active</strong> — Format checks only. Go to <strong>Settings → Perfios KYC Configuration</strong> to enable live verification.</span>
            </div>
          )}

          {/* Partners table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[700px]">
                <thead className="border-b border-border bg-muted/20">
                  <tr>
                    {["Partner", "Type", "PAN", "Aadhaar", "Bank", "KYC Status", "Action"].map(h => (
                      <th key={h} className="px-4 py-3.5 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
                  )}
                  {!loading && partners.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-muted-foreground text-sm">No partners found.</td></tr>
                  )}
                  {!loading && partners.map(p => (
                    <tr key={p.id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`kyc-row-${p.id}`}>
                      <td className="px-4 py-3.5">
                        <div className="font-medium text-foreground">{p.name}</div>
                        <div className="text-[11px] text-muted-foreground">{p.email}</div>
                        {p.kyc_status === "admin_review" && p.kyc_review_reason && (
                          <div className="mt-1 text-[10px] text-amber-600 bg-amber-500/8 border border-amber-500/20 rounded px-2 py-1 max-w-[260px]">
                            {p.kyc_review_reason}
                          </div>
                        )}
                        {p.kyc_status === "rejected" && p.kyc_reject_reason && (
                          <div className="mt-1 text-[10px] text-red-600 bg-red-500/8 border border-red-500/20 rounded px-2 py-1 max-w-[260px]">
                            {p.kyc_reject_reason}
                          </div>
                        )}
                        {p.kyc_status === "query_raised" && p.kyc_query_note && (
                          <div className="mt-1 text-[10px] text-blue-600 bg-blue-500/8 border border-blue-500/20 rounded px-2 py-1 max-w-[260px]">
                            Query: {p.kyc_query_note}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-muted-foreground text-[11px]">
                        {PARTNER_TYPE_LABELS[p.partner_type] || p.partner_type}
                      </td>
                      <td className="px-4 py-3.5"><StatusBadge verified={p.pan_verified} label={p.pan ? "PAN ✓" : "PAN"} /></td>
                      <td className="px-4 py-3.5"><StatusBadge verified={p.aadhaar_verified} label="Aadhaar" /></td>
                      <td className="px-4 py-3.5"><StatusBadge verified={p.bank_verified} label="Bank" /></td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          {kycIcon(p)}
                          <span className={`text-[10px] font-medium capitalize ${
                            p.kyc_status === "verified"      ? "text-emerald-600"
                            : p.kyc_status === "admin_review" ? "text-amber-600"
                            : p.kyc_status === "rejected"     ? "text-red-600"
                            : p.kyc_status === "query_raised" ? "text-blue-600"
                            : "text-muted-foreground"
                          }`}>
                            {p.kyc_status === "verified"       ? "Verified"
                             : p.kyc_status === "admin_review" ? "Review"
                             : p.kyc_status === "rejected"     ? "Rejected"
                             : p.kyc_status === "query_raised" ? "Query"
                             : (p.pan_verified || p.aadhaar_verified || p.bank_verified) ? "Partial"
                             : "Pending"}
                          </span>
                          {p.kyc_review_score && p.kyc_status === "admin_review" && (
                            <span className="text-[9px] text-amber-500">({Math.round(p.kyc_review_score * 100)}%)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => openModal(p)} data-testid={`kyc-action-${p.id}`}
                            className="text-[11px] px-3 py-1.5 border border-primary/40 text-primary rounded-lg hover:bg-primary/10 transition-all">
                            Review
                          </button>
                          {p.kyc_status === "admin_review" && (
                            <>
                              <button
                                onClick={() => handleAdminDecide(p.id, "approve")}
                                disabled={reviewDeciding === p.id}
                                data-testid={`kyc-approve-${p.id}`}
                                title="Quick Approve"
                                className="flex items-center justify-center w-7 h-7 bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 rounded-lg hover:bg-emerald-500/20 transition-all disabled:opacity-50">
                                <ThumbsUp className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleAdminDecide(p.id, "reject")}
                                disabled={reviewDeciding === p.id}
                                data-testid={`kyc-reject-${p.id}`}
                                title="Quick Reject"
                                className="flex items-center justify-center w-7 h-7 bg-red-500/10 border border-red-500/30 text-red-700 rounded-lg hover:bg-red-500/20 transition-all disabled:opacity-50">
                                <ThumbsDown className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Audit Log view ── */}
      {mainTab === "log" && (
        <>
          {/* Log KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <KpiCard label="Total Attempts" value={logSummary.total} icon={ScrollText} color="bg-primary/10 text-primary" />
            <KpiCard label="Passed" value={logSummary.pass_count} icon={CheckCircle2} color="bg-emerald-500/10 text-emerald-600" />
            <KpiCard label="Failed" value={logSummary.fail_count} icon={X} color="bg-red-500/10 text-red-600" />
          </div>

          {/* Filters row */}
          <div className="flex flex-wrap gap-3 items-center" data-testid="log-filters">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search partner name or email…"
                value={logSearch}
                onChange={e => setLogSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-card border border-border rounded-xl text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                data-testid="log-search"
              />
            </div>

            {/* Type filter */}
            <div className="flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <div className="flex bg-muted rounded-lg p-0.5 gap-0.5">
                {["all", "pan", "bank", "aadhaar"].map(t => (
                  <button key={t} onClick={() => setLogTypeFilter(t)}
                    data-testid={`log-type-filter-${t}`}
                    className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all capitalize ${logTypeFilter === t ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                    {t === "all" ? "All Types" : t.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Result filter */}
            <div className="flex bg-muted rounded-lg p-0.5 gap-0.5">
              {["all", "pass", "fail"].map(r => (
                <button key={r} onClick={() => setLogResultFilter(r)}
                  data-testid={`log-result-filter-${r}`}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all capitalize ${logResultFilter === r ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                  {r === "all" ? "All Results" : r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>

            {/* Actions */}
            <button onClick={loadLogs} disabled={logLoading}
              className="p-2 border border-border rounded-lg hover:bg-muted transition-all disabled:opacity-50" title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 text-muted-foreground ${logLoading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={exportCSV} data-testid="export-log-csv-btn"
              className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-lg text-[12px] font-medium hover:bg-muted transition-all">
              <Download className="w-3.5 h-3.5" />Export CSV
            </button>
          </div>

          {/* Log table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="audit-log-table">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[650px]">
                <thead className="border-b border-border bg-muted/20">
                  <tr>
                    {["Partner", "Type", "Provider", "Result", "Score", "Timestamp"].map(h => (
                      <th key={h} className="px-4 py-3.5 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logLoading && (
                    <tr><td colSpan={6} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
                  )}
                  {!logLoading && filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-14 text-center">
                        <ScrollText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                        <div className="text-sm text-muted-foreground">No verification attempts yet.</div>
                        <div className="text-xs text-muted-foreground mt-1">Attempts will appear here after running PAN, Bank or Aadhaar verification.</div>
                      </td>
                    </tr>
                  )}
                  {!logLoading && filteredLogs.map(log => <LogRow key={log.id} log={log} />)}
                </tbody>
              </table>
            </div>
            {filteredLogs.length > 0 && (
              <div className="px-4 py-2.5 border-t border-border bg-muted/10 text-[11px] text-muted-foreground">
                Showing {filteredLogs.length} of {logSummary.total} entries · Click a row to expand details
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Admin Review Modal ── */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-xl shadow-2xl max-h-[90vh] flex flex-col"
            data-testid="kyc-review-modal">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  selected.kyc_status === "verified"      ? "bg-emerald-500/10"
                  : selected.kyc_status === "admin_review" ? "bg-amber-500/10"
                  : selected.kyc_status === "rejected"    ? "bg-red-500/10"
                  : selected.kyc_status === "query_raised" ? "bg-blue-500/10"
                  : "bg-muted"
                }`}>
                  {kycIcon(selected)}
                </div>
                <div>
                  <div className="font-semibold text-foreground">{selected.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {selected.email} · {PARTNER_TYPE_LABELS[selected.partner_type] || selected.partner_type}
                  </div>
                </div>
              </div>
              <button onClick={() => setSelected(null)} data-testid="kyc-modal-close">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {/* Review body — scrollable */}
            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Verification status chips */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "PAN",     verified: selected.pan_verified,     date: selected.kyc_pan_verified_at,     error: selected.pan_verify_error },
                  { label: "Aadhaar", verified: selected.aadhaar_verified, date: selected.kyc_aadhaar_verified_at, error: null },
                  { label: "Bank",    verified: selected.bank_verified,    date: selected.kyc_bank_verified_at,    error: selected.bank_verify_error },
                ].map(({ label, verified, date, error }) => (
                  <div key={label} className={`text-center p-3 rounded-xl border ${
                    verified ? "bg-emerald-500/5 border-emerald-500/20"
                    : error   ? "bg-red-500/5 border-red-500/20"
                    : "bg-muted/20 border-border/60"
                  }`}>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
                    {verified
                      ? <span className="flex items-center justify-center gap-1 text-[11px] text-emerald-600 font-medium"><CheckCircle2 className="w-3 h-3" />Verified</span>
                      : error
                        ? <span className="flex items-center justify-center gap-1 text-[11px] text-red-500 font-medium"><AlertTriangle className="w-3 h-3" />Failed</span>
                        : <span className="text-[11px] text-muted-foreground/60">Not verified</span>
                    }
                    {verified && date && (
                      <div className="text-[9px] text-muted-foreground/50 mt-0.5">
                        {fmtDateShort(date)}
                      </div>
                    )}
                    {!verified && error && (
                      <div className="text-[9px] text-red-400 mt-1 leading-tight px-1 line-clamp-2" title={error}>
                        {error.length > 60 ? error.slice(0, 57) + "…" : error}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* 3-way Name Comparison — primary view */}
              {(selected.pan_name_on_card || selected.bank_account_holder_verified) && (
                <CrossMatchPanel
                  partnerName={selected.name}
                  panName={selected.pan_name_on_card}
                  bankName={selected.bank_account_holder_verified}
                  kycStatus={selected.kyc_status}
                  score={selected.kyc_review_score ?? selected.kyc_reject_score}
                  reason={selected.kyc_review_reason || selected.kyc_reject_reason || selected.kyc_query_note}
                />
              )}

              {/* PAN Details */}
              {(selected.pan || selected.pan_name_on_card) && (
                <KycComparePanel
                  title="PAN Card Details"
                  rows={[
                    selected.pan ? {
                      label: "PAN Number",
                      submitted: selected.pan,
                      govt: selected.pan_verified ? "✓ Valid" : "—",
                      match: selected.pan_verified ? true : null,
                    } : null,
                    selected.pan_name_on_card ? {
                      label: "Name",
                      submitted: selected.name,
                      govt: selected.pan_name_on_card,
                      match: selected.pan_name_match,
                    } : null,
                    selected.pan_type ? {
                      label: "PAN Type",
                      govt: selected.pan_type,
                      match: null,
                      govtOnly: true,
                    } : null,
                  ].filter(Boolean)}
                  status={selected.pan_verified ? "verified" : null}
                  provider={selected.pan_provider}
                />
              )}

              {/* Bank Details */}
              {(selected.bank_account_number || selected.bank_account_holder_verified) && (
                <>
                  <KycComparePanel
                    title="Bank Account Details"
                    rows={[
                      selected.bank_account_number ? {
                        label: "Account Number",
                        submitted: "•".repeat(Math.max(0, String(selected.bank_account_number).length - 4)) + String(selected.bank_account_number).slice(-4),
                        govt: selected.bank_verified ? "✓ Active" : "—",
                        match: selected.bank_verified ? true : null,
                      } : null,
                      selected.bank_ifsc ? {
                        label: "IFSC Code",
                        submitted: selected.bank_ifsc,
                        govt: selected.bank_verified ? "✓ Valid" : "—",
                        match: selected.bank_verified ? true : null,
                      } : null,
                      selected.bank_account_holder_verified ? {
                        label: "Account Holder Name",
                        submitted: selected.name,
                        govt: selected.bank_account_holder_verified,
                        match: selected.bank_name_match,
                      } : null,
                      selected.bank_status ? {
                        label: "Bank Account Status",
                        govt: selected.bank_status,
                        match: null,
                        govtOnly: true,
                      } : null,
                    ].filter(Boolean)}
                    status={selected.bank_verified ? "verified" : null}
                    provider={selected.bank_provider}
                  />
                  {!selected.bank_verified && selected.bank_verify_error && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/5 border border-red-500/20 text-[11px] text-red-600">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span><strong>Verification failed:</strong> {selected.bank_verify_error}</span>
                    </div>
                  )}
                </>
              )}

              {/* No data state */}
              {!selected.pan && !selected.pan_name_on_card && !selected.bank_account_number && !selected.bank_account_holder_verified && (
                <div className="text-center py-10 text-muted-foreground">
                  <ShieldAlert className="w-9 h-9 mx-auto mb-2.5 opacity-25" />
                  <p className="text-sm">No KYC data captured yet.</p>
                  <p className="text-xs mt-1 opacity-60">Waiting for partner to submit KYC details via the portal.</p>
                </div>
              )}
            </div>

            {/* Action bar */}
            {selected.kyc_status !== "verified" ? (
              <div className="px-5 py-4 border-t border-border shrink-0 space-y-3">
                {/* Re-evaluate button — show whenever KYC is not yet verified and data exists */}
                {selected.kyc_status !== "verified" && (selected.pan || (selected.bank_account_number && selected.bank_ifsc)) && !showQueryInput && !showRejectInput && (
                  <button
                    onClick={handleReVerify}
                    disabled={reVerifying}
                    data-testid="kyc-modal-reverify"
                    className="w-full flex items-center justify-center gap-2 py-2 border border-primary/40 text-primary rounded-xl text-[12px] font-medium hover:bg-primary/5 disabled:opacity-50 transition-colors">
                    <RefreshCw className={`w-3.5 h-3.5 ${reVerifying ? "animate-spin" : ""}`} />
                    {reVerifying ? "Running verification…"
                      : (!selected.pan_verified || !selected.bank_verified) ? "Re-verify KYC (PAN + Bank)"
                      : "Re-run Cross-Match"}
                  </button>
                )}
                {showQueryInput && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Query for Partner</div>
                    <textarea
                      value={queryNote}
                      onChange={e => setQueryNote(e.target.value)}
                      placeholder="Describe what information or correction you need from the partner…"
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-blue-300 rounded-lg text-[12px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid="kyc-query-input"
                      autoFocus
                    />
                  </div>
                )}
                {showRejectInput && (
                  <div className="space-y-1.5">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Rejection Reason (shown to partner)</div>
                    <textarea
                      value={rejectNote}
                      onChange={e => setRejectNote(e.target.value)}
                      placeholder="State why KYC is being rejected…"
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-red-300 rounded-lg text-[12px] resize-none focus:outline-none focus:ring-1 focus:ring-red-400"
                      data-testid="kyc-reject-input"
                      autoFocus
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {/* Approve — hidden when reject input is open */}
                  {!showRejectInput && !showQueryInput && (
                    <button
                      onClick={() => handleAdminDecide(selected.id, "approve")}
                      disabled={reviewDeciding === selected.id}
                      data-testid="kyc-modal-approve"
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-emerald-600 text-white rounded-xl text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                      <ThumbsUp className="w-3.5 h-3.5" />Approve
                    </button>
                  )}
                  {/* Query */}
                  {!showRejectInput && (
                    showQueryInput ? (
                      <button
                        onClick={() => handleAdminDecide(selected.id, "query", queryNote)}
                        disabled={!queryNote.trim() || reviewDeciding === selected.id}
                        data-testid="kyc-modal-send-query"
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-blue-600 text-white rounded-xl text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
                        <MessageSquare className="w-3.5 h-3.5" />Send Query
                      </button>
                    ) : (
                      <button
                        onClick={() => { setShowQueryInput(true); setShowRejectInput(false); }}
                        data-testid="kyc-modal-query"
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-blue-300 text-blue-600 rounded-xl text-[13px] font-medium hover:bg-blue-50 transition-colors">
                        <MessageSquare className="w-3.5 h-3.5" />Query
                      </button>
                    )
                  )}
                  {/* Reject */}
                  {!showQueryInput && (
                    showRejectInput ? (
                      <button
                        onClick={() => handleAdminDecide(selected.id, "reject", rejectNote)}
                        disabled={reviewDeciding === selected.id}
                        data-testid="kyc-modal-confirm-reject"
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-600 text-white rounded-xl text-[13px] font-medium hover:bg-red-700 disabled:opacity-50 transition-colors">
                        Confirm Reject
                      </button>
                    ) : (
                      <button
                        onClick={() => { setShowRejectInput(true); setShowQueryInput(false); }}
                        data-testid="kyc-modal-reject"
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-red-300 text-red-600 rounded-xl text-[13px] font-medium hover:bg-red-50 transition-colors">
                        <ThumbsDown className="w-3.5 h-3.5" />Reject
                      </button>
                    )
                  )}
                </div>
                {(showQueryInput || showRejectInput) && (
                  <button
                    onClick={() => { setShowQueryInput(false); setShowRejectInput(false); setQueryNote(""); setRejectNote(""); }}
                    className="w-full text-[11px] text-muted-foreground hover:text-foreground transition-colors py-1">
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <div className="px-5 py-3.5 border-t border-border shrink-0 flex items-center gap-2 text-[12px] text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />KYC verified — no further action required
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
